/** Retained-history display projection, never a balance or pricing authority. Decimal strings stay exact. */
export interface MonitorCollectionUsage {
    stage: 'collection' | 'comments';
    jobs: number; imported_jobs: number; archived_records: string | null;
}
export interface MonitorAnalysisUsage {
    stage: 'summary' | 'creative' | 'vision' | 'transcription';
    groups: { not_started: number; settled: number; pending: number; unavailable: number };
    confirmed_work_units: string | null;
}
export interface MonitorSupplementUsage {
    stage: 'media_inspection' | 'media_preparation' | 'landing'; jobs: number; processed_jobs: number; recorded_jobs: number;
}
export interface MarketMonitorUsage {
    schema_version: 'market-monitor-usage/v2'; monitor_id: string; as_of: string; history_scope: 'retained_records';
    collections: MonitorCollectionUsage[]; analysis: MonitorAnalysisUsage[]; supplements: MonitorSupplementUsage[];
    confirmed_work_units: string | null;
}
function requireValue(ok: unknown): asserts ok { if (!ok) throw new Error('Invalid monitor usage'); }
function object(value: unknown, keys: string): Record<string, unknown> {
    requireValue(value && typeof value === 'object' && !Array.isArray(value));
    const row = value as Record<string, unknown>, expected = keys.split(' ');
    requireValue(Object.keys(row).length === expected.length && expected.every(key => Object.prototype.hasOwnProperty.call(row, key))); return row;
}
function count(value: unknown): number { requireValue(typeof value === 'number' && Number.isSafeInteger(value) && value >= 0); return value; }
function decimal(value: unknown, scale: number): string | null {
    requireValue(value === null || typeof value === 'string' && new RegExp(`^(0|[1-9]\\d{0,29})(\\.\\d{1,${Math.max(scale, 1)}})?$`).test(value)
        && (scale > 0 || !value.includes('.'))); return value as string | null;
}
const scaled = (value: string) => { const [whole, fraction = ''] = value.split('.'); return BigInt(whole + fraction.padEnd(5, '0')); };
export function validateMonitorUsage(value: unknown, monitor: string): MarketMonitorUsage {
    const rootKeys = 'schema_version monitor_id as_of history_scope collections analysis supplements confirmed_work_units';
    const hasCollectionUnits = value !== null && typeof value === 'object' && !Array.isArray(value)
        && Object.prototype.hasOwnProperty.call(value, 'collection_confirmed_work_units');
    const root = object(value, hasCollectionUnits ? `${rootKeys} collection_confirmed_work_units` : rootKeys);
    const collectionUnits = hasCollectionUnits ? decimal(root.collection_confirmed_work_units, 5) : null;
    const legacy = root.schema_version === 'market-monitor-usage/v1';
    requireValue((legacy || root.schema_version === 'market-monitor-usage/v2') && root.monitor_id === monitor && root.history_scope === 'retained_records');
    requireValue(typeof root.as_of === 'string' && /^\d{4}-\d{2}-\d{2}T.+(?:Z|[+-]\d{2}:\d{2})$/.test(root.as_of) && Number.isFinite(Date.parse(root.as_of)));
    requireValue(Array.isArray(root.collections) && root.collections.length <= (legacy ? 6 : 2));
    const collectionKeys = new Set<string>();
    const collections = new Map<string, MonitorCollectionUsage>();
    for (const value of root.collections) {
        const row = legacy ? legacyFields(value, 'stage jobs imported_jobs archived_records provider')
            : object(value, 'stage jobs imported_jobs archived_records');
        requireValue(['collection', 'comments'].includes(String(row.stage)));
        if (legacy) requireValue(typeof row.provider === 'string' && row.provider.length > 0);
        const key = legacy ? `${row.stage}:${row.provider}` : String(row.stage);
        requireValue(!collectionKeys.has(key)); collectionKeys.add(key);
        const jobs = count(row.jobs), imported = count(row.imported_jobs), records = decimal(row.archived_records, 0);
        requireValue(jobs > 0 && imported <= jobs && (records === null) === (imported === 0));
        const stage = row.stage as MonitorCollectionUsage['stage'];
        const total = collections.get(stage) ?? { stage, jobs: 0, imported_jobs: 0, archived_records: null };
        total.jobs = count(total.jobs + jobs); total.imported_jobs = count(total.imported_jobs + imported);
        if (records !== null) total.archived_records = decimal((BigInt(total.archived_records ?? '0') + BigInt(records)).toString(), 0);
        collections.set(stage, total);
    }
    requireValue(Array.isArray(root.analysis) && root.analysis.length === 4);
    const stages = new Set<string>(); let subtotal: bigint | null = collectionUnits === null ? null : scaled(collectionUnits);
    for (const value of root.analysis) {
        const row = object(value, 'stage groups confirmed_work_units'), name = String(row.stage);
        requireValue(['summary', 'creative', 'vision', 'transcription'].includes(name) && !stages.has(name)); stages.add(name);
        const groups = object(row.groups, 'not_started settled pending unavailable'); Object.values(groups).forEach(count);
        const units = decimal(row.confirmed_work_units, 5), possible = count(groups.settled) + count(groups.pending);
        requireValue(units === null ? groups.settled === 0 : possible > 0 && scaled(units) > BigInt(0));
        if (units !== null) subtotal = (subtotal ?? BigInt(0)) + scaled(units);
    }
    const total = decimal(root.confirmed_work_units, 5);
    requireValue(total === null ? subtotal === null : subtotal !== null && scaled(total) === subtotal);
    requireValue(Array.isArray(root.supplements) && root.supplements.length <= 3);
    const supplements = new Set<string>();
    const publicSupplements: MonitorSupplementUsage[] = [];
    for (const value of root.supplements) {
        const keys = 'stage jobs processed_jobs recorded_jobs';
        const row = legacy ? legacyFields(value, keys) : object(value, keys), name = String(row.stage);
        requireValue(['media_inspection', 'media_preparation', 'landing'].includes(name) && !supplements.has(name)); supplements.add(name);
        requireValue(count(row.jobs) > 0 && count(row.processed_jobs) <= count(row.jobs) && count(row.recorded_jobs) <= count(row.jobs));
        publicSupplements.push(row as unknown as MonitorSupplementUsage);
    }
    // Never return the legacy wire object: supplier/cost facts must not enter UI or expert state.
    const { collection_confirmed_work_units: _collectionUnits, ...publicRoot } = root;
    return { ...publicRoot, schema_version: 'market-monitor-usage/v2', collections: [...collections.values()], supplements: publicSupplements } as MarketMonitorUsage;
}
function legacyFields(value: unknown, keys: string): Record<string, unknown> {
    requireValue(value && typeof value === 'object' && !Array.isArray(value));
    return Object.fromEntries(keys.split(' ').map(key => [key, (value as Record<string, unknown>)[key]]));
}
